import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Exercise, ClientExerciseExceptionCreate } from '../types';

interface AddExceptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise;
  clientName: string;
  onSubmit: (data: ClientExerciseExceptionCreate) => void;
  isLoading?: boolean;
}

const AddExceptionModal: React.FC<AddExceptionModalProps> = ({
  isOpen,
  onClose,
  exercise,
  clientName,
  onSubmit,
  isLoading = false
}) => {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      onSubmit({
        exercise_id: exercise.id,
        reason: reason.trim()
      });
      setReason('');
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Добавить исключение
              </h3>
              <p className="text-sm text-gray-500">
                Упражнение будет исключено для {clientName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Содержимое */}
        <div className="p-6">
          {/* Информация об упражнении */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">
              Упражнение: {exercise.name}
            </h4>
            {exercise.description && (
              <p className="text-sm text-gray-600">
                {exercise.description}
              </p>
            )}
            {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
              <div className="mt-2">
                <span className="text-xs text-gray-500">Группы мышц: </span>
                <span className="text-xs text-gray-700">
                  {exercise.muscle_groups.map(g => g.name).join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Форма */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                Причина исключения *
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Например: боль в плече, травма колена, противопоказания..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
                required
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Укажите причину, по которой это упражнение противопоказано клиенту
              </p>
            </div>

            {/* Кнопки */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={isLoading || !reason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Добавление...</span>
                  </div>
                ) : (
                  'Добавить исключение'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddExceptionModal; 